import { PLAYER_H, PLAYER_W, SUB, TILE_PX, VIEW_H_PX, VIEW_W_PX } from '../shared/constants.ts';
import { T_SOLID, T_SPIKE } from '../shared/level.ts';
import type { Sim } from '../shared/sim.ts';

/** Drops in flight, in world pixels. */
interface Drop {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  life: number;
}

/** Where a drop landed: it stays on the level until the next run. */
interface Stain {
  x: number;
  y: number;
  r: number;
  /** Stuck to the side of a block: it slowly runs down. */
  side: boolean;
  drip: number;
  dripMax: number;
}

/** Bits that bounce instead of sticking: the eyes, and a scrap of scarf. */
interface Bit {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  kind: 'eye' | 'scarf';
}

/** A splat on the camera itself, in screen pixels. */
interface Lens {
  x: number;
  y: number;
  r: number;
  blobs: [number, number, number][];
  dripMax: number;
  at: number;
}

const GOO = '#2f7bff';
const GOO_DARK = '#1c4fc4';
const GOO_LIGHT = '#8cc8ff';
const GRAVITY = 520;
const LENS_HOLD = 2600;
const LENS_FADE = 1400;

/**
 * Blue goo for when a run ends: a burst that sticks to the blocks it hits,
 * the runner's eyes bouncing away, and splats on the "camera" that slide down
 * and fade. Cosmetic only (Math.random is fine here); it shapes itself to how
 * the runner died: a geyser from the void, a smear on the left edge, a fountain
 * on the spikes.
 */
export class Gore {
  private key = '';
  private drops: Drop[] = [];
  private stains: Stain[] = [];
  private bits: Bit[] = [];
  private lens: Lens[] = [];
  private shakeT = 0;
  private flashT = 0;

  /** Forget the previous run's mess when a new run is shown. */
  reset(key: string): void {
    if (key === this.key) return;
    this.key = key;
    this.drops = [];
    this.stains = [];
    this.bits = [];
    this.lens = [];
    this.shakeT = 0;
    this.flashT = 0;
  }

  burst(sim: Sim, cam: number, now: number): void {
    const pw = PLAYER_W / SUB;
    const ph = PLAYER_H / SUB;
    let x = sim.x / SUB + pw / 2;
    let y = sim.y / SUB + ph / 2;
    const reason = sim.deathReason;
    // Initial speeds per death: [vx min, vx max, vy min, vy max].
    let v: [number, number, number, number] = [-200, 200, -320, -60];
    let n = 130;
    if (reason === 'fell') {
      y = VIEW_H_PX + 2;
      v = [-130, 130, -430, -200];
      n = 100;
    } else if (reason === 'left behind') {
      x = cam + 1;
      v = [30, 270, -260, -30];
    } else if (reason === 'rained on') {
      y = sim.y / SUB + 2;
      v = [-230, 230, -260, -40];
    }
    for (let i = 0; i < n; i++) {
      const big = Math.random() < 0.3;
      this.drops.push({ x: x + rnd(-3, 3), y: y + rnd(-3, 3), vx: rnd(v[0], v[1]), vy: rnd(v[2], v[3]), r: big ? rnd(1.8, 3.2) : rnd(0.8, 1.6), life: 3 });
    }
    // The eyes (and the scarf) go flying.
    for (let i = 0; i < 3; i++) {
      const up = reason === 'fell' ? rnd(-330, -260) : rnd(-260, -160);
      const side = reason === 'left behind' ? rnd(60, 140) : rnd(-110, 110);
      this.bits.push({ x, y, vx: side, vy: up, rot: 0, vr: rnd(-14, 14), kind: i < 2 ? 'eye' : 'scarf' });
    }
    // Splats on the camera, around where it happened.
    const sx = x - cam;
    const sy = Math.min(y, VIEW_H_PX - 20);
    const count = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      let lx = sx + rnd(-90, 90);
      let ly = sy + rnd(-90, 40);
      if (reason === 'left behind' && i < 2) {
        lx = rnd(0, 14);
        ly = sy + rnd(-30, 30);
      }
      this.splat(clamp(lx, 6, VIEW_W_PX - 6), clamp(ly, 8, VIEW_H_PX - 10), i === 0 ? rnd(20, 30) : rnd(8, 18), now + i * rnd(30, 70));
    }
    this.shakeT = 0.45;
    this.flashT = 0.12;
  }

  private splat(x: number, y: number, r: number, at: number): void {
    const blobs: [number, number, number][] = [];
    const n = 6 + Math.floor(Math.random() * 6);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const d = r * rnd(0.7, 1.9);
      blobs.push([Math.cos(a) * d, Math.sin(a) * d, r * (d > r * 1.4 ? rnd(0.08, 0.18) : rnd(0.25, 0.5))]);
    }
    this.lens.push({ x, y, r, blobs, dripMax: Math.random() < 0.7 ? r * rnd(0.8, 2.4) : 0, at });
  }

  /** Camera offset for the screen shake (world drawing only, not the HUD). */
  shake(dt: number): [number, number] {
    if (this.shakeT <= 0) return [0, 0];
    this.shakeT = Math.max(0, this.shakeT - dt);
    const a = (this.shakeT / 0.45) ** 2 * 5;
    return [rnd(-a, a), rnd(-a, a)];
  }

  /** Stains, flying drops and bits, in world space (call between the level and the player). */
  drawWorld(ctx: CanvasRenderingContext2D, sim: Sim, cam: number, dt: number): void {
    this.update(sim, dt);
    for (const s of this.stains) {
      const x = s.x - cam;
      if (x < -20 || x > VIEW_W_PX + 20) continue;
      ctx.fillStyle = GOO_DARK;
      if (s.side) {
        ctx.fillRect(x - s.r * 0.6, s.y, s.r * 1.2, s.drip);
        circle(ctx, x, s.y + s.drip, s.r * 0.75);
      } else {
        ellipse(ctx, x, s.y, s.r * 2.2, s.r * 0.9);
        if (s.drip > 0.5) {
          ctx.fillRect(x - s.r * 0.35, s.y, s.r * 0.7, s.drip);
          circle(ctx, x, s.y + s.drip, s.r * 0.5);
        }
      }
      ctx.fillStyle = GOO;
      ellipse(ctx, x - s.r * 0.3, s.y - s.r * 0.15, s.r * 1.2, s.r * 0.45);
    }
    ctx.fillStyle = GOO;
    for (const d of this.drops) circle(ctx, d.x - cam, d.y, d.r);
    for (const b of this.bits) {
      ctx.save();
      ctx.translate(b.x - cam, b.y);
      ctx.rotate(b.rot);
      if (b.kind === 'eye') {
        ctx.fillStyle = '#fff';
        ctx.fillRect(-1.2, -1.6, 2.4, 3.2);
        ctx.fillStyle = '#111';
        ctx.fillRect(-0.1, -0.6, 1, 1.4);
      } else {
        ctx.fillStyle = '#ff4d6d';
        ctx.fillRect(-2.5, -0.7, 5, 1.4);
      }
      ctx.restore();
    }
  }

  /** Splats on the camera, plus the impact flash (call after the world, before the HUD). */
  drawLens(ctx: CanvasRenderingContext2D, now: number, dt: number): void {
    if (this.flashT > 0) {
      this.flashT -= dt;
      ctx.fillStyle = `rgba(200, 225, 255, ${Math.max(0, this.flashT / 0.12) * 0.45})`;
      ctx.fillRect(-10, -10, VIEW_W_PX + 20, VIEW_H_PX + 20);
    }
    this.lens = this.lens.filter((l) => now - l.at < LENS_HOLD + LENS_FADE);
    for (const l of this.lens) {
      const age = now - l.at;
      if (age < 0) continue;
      const pop = Math.min(1, age / 70);
      const alpha = 0.85 * Math.min(1, 1 - (age - LENS_HOLD) / LENS_FADE);
      const slide = Math.max(0, age - 300) / 1000; // the whole splat creeps down the glass
      const y = l.y + slide * 4;
      const drip = Math.min(l.dripMax, slide * l.dripMax * 0.8);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = GOO;
      circle(ctx, l.x, y, l.r * pop);
      for (const [dx, dy, r] of l.blobs) circle(ctx, l.x + dx * pop, y + dy * pop, r * pop);
      if (drip > 0) {
        const w = Math.max(1.2, l.r * 0.16);
        ctx.fillRect(l.x - w / 2, y, w, l.r * 0.6 + drip);
        circle(ctx, l.x, y + l.r * 0.6 + drip, w * 0.9);
      }
      // A glint, so it reads as goo on the glass.
      ctx.fillStyle = GOO_LIGHT;
      ctx.globalAlpha = alpha * 0.7;
      ellipse(ctx, l.x - l.r * 0.35, y - l.r * 0.4, l.r * 0.28 * pop, l.r * 0.16 * pop);
    }
    ctx.globalAlpha = 1;
  }

  private update(sim: Sim, dt: number): void {
    const keep: Drop[] = [];
    for (const d of this.drops) {
      d.life -= dt;
      const px = d.x;
      const py = d.y;
      d.vy += GRAVITY * dt;
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      if (d.life <= 0 || d.y > VIEW_H_PX + 30) continue;
      const hit = surface(sim, d.x, d.y);
      if (hit === null) {
        keep.push(d);
        continue;
      }
      // Landed on top, or splashed against the side of a block?
      const fromAbove = surface(sim, d.x, py) === null && py <= hit;
      if (fromAbove) this.stains.push({ x: d.x, y: hit, r: d.r, side: false, drip: 0, dripMax: Math.random() < 0.2 ? rnd(2, 6) : 0 });
      else {
        const c = Math.floor(d.x / TILE_PX);
        const edge = px < d.x ? c * TILE_PX : (c + 1) * TILE_PX;
        this.stains.push({ x: edge, y: d.y, r: d.r, side: true, drip: 0, dripMax: rnd(4, 14) });
      }
    }
    this.drops = keep;
    for (const s of this.stains) if (s.drip < s.dripMax) s.drip = Math.min(s.dripMax, s.drip + dt * 3);
    if (this.stains.length > 400) this.stains.splice(0, this.stains.length - 400);

    for (const b of this.bits) {
      b.vy += GRAVITY * dt;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.rot += b.vr * dt;
      const top = surface(sim, b.x, b.y + 1.5);
      if (top !== null && b.vy > 0) {
        b.y = top - 1.5;
        b.vy = Math.abs(b.vy) > 60 ? -b.vy * 0.45 : 0;
        b.vx *= 0.7;
        b.vr *= 0.6;
      }
    }
    this.bits = this.bits.filter((b) => b.y < VIEW_H_PX + 20);
  }
}

/** The y of the surface at this point (block top or spike tips), or null in open air. */
function surface(sim: Sim, x: number, y: number): number | null {
  const c = Math.floor(x / TILE_PX);
  const r = Math.floor(y / TILE_PX);
  const t = sim.level.tile(c, r);
  if (t === T_SOLID) return r * TILE_PX;
  if (t === T_SPIKE && y - r * TILE_PX >= 8) return r * TILE_PX + 8;
  return null;
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, Math.max(0, r), 0, Math.PI * 2);
  ctx.fill();
}

function ellipse(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number): void {
  ctx.beginPath();
  ctx.ellipse(x, y, Math.max(0, rx), Math.max(0, ry), 0, 0, Math.PI * 2);
  ctx.fill();
}

function rnd(a: number, b: number): number {
  return a + Math.random() * (b - a);
}

function clamp(v: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, v));
}
